import React, {ChangeEvent, useRef, useState} from 'react'
import {
  Appearance,
  Button,
  ButtonShape,
  ButtonType,
  Columns,
  ComponentColor,
  ComponentSize,
  ComponentStatus,
  Form,
  Grid,
  Input,
  InputType,
  Popover,
  PopoverInteraction,
  PopoverPosition,
  PopoverRef,
  VisibilityInput,
} from '@influxdata/clockface'

interface Props {
  buttonStatus: ComponentStatus
  confirmPasswordError?: string
  confirmPassword: string
  emailError?: string
  email: string
  firstName: string
  firstNameError?: string
  lastName: string
  lastNameError?: string
  passwordError?: string
  password: string
  handleInputChange: (e: ChangeEvent<HTMLInputElement>) => void
}

const SignUpForm = ({
  buttonStatus,
  confirmPasswordError,
  confirmPassword,
  emailError,
  email,
  firstName,
  firstNameError,
  lastName,
  lastNameError,
  passwordError,
  password,
  handleInputChange,
}: Props): JSX.Element => {
  const passwordTriggerRef = useRef<HTMLDivElement>(null)
  const passwordRef = useRef<PopoverRef>(null)
  const [passwordReqsVisible, togglePasswordReqs] = useState(false)
  const [visible, toggleIcon] = useState(false)
  return (
    <>
      <Grid>
        <Grid.Row className="sign-up--form-padded-row">
          <Grid.Column widthXS={Columns.Six}>
            <Form.Element
              label="First Name"
              htmlFor="firstName"
              required={true}
              errorMessage={firstNameError}
            >
              <Input
                id="firstName"
                testID="firstName--input"
                value={firstName}
                autoFocus={true}
                size={ComponentSize.Large}
                status={
                  firstNameError
                    ? ComponentStatus.Error
                    : ComponentStatus.Default
                }
                onChange={handleInputChange}
              />
            </Form.Element>
          </Grid.Column>
          <Grid.Column widthXS={Columns.Six}>
            <Form.Element
              label="Last Name"
              htmlFor="lastName"
              required={true}
              errorMessage={lastNameError}
            >
              <Input
                id="lastName"
                testID="lastName--input"
                value={lastName}
                size={ComponentSize.Large}
                status={
                  lastNameError
                    ? ComponentStatus.Error
                    : ComponentStatus.Default
                }
                onChange={handleInputChange}
              />
            </Form.Element>
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column widthXS={Columns.Twelve}>
            <Form.Element
              label="Work Email Address"
              htmlFor="email"
              required={true}
              errorMessage={emailError}
            >
              <Input
                id="email"
                testID="email--input"
                value={email}
                type={InputType.Email}
                size={ComponentSize.Large}
                status={
                  emailError ? ComponentStatus.Error : ComponentStatus.Default
                }
                onChange={handleInputChange}
              />
            </Form.Element>
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column widthXS={Columns.Twelve}>
            <Form.Element
              label="Password"
              htmlFor="password"
              required={true}
              errorMessage={passwordError}
            >
              <div ref={passwordTriggerRef}>
                <VisibilityInput
                  id="password"
                  testID="password--input"
                  value={password}
                  size={ComponentSize.Large}
                  onChange={handleInputChange}
                  visible={visible}
                  status={
                    passwordError
                      ? ComponentStatus.Error
                      : ComponentStatus.Default
                  }
                  onToggleClick={() => toggleIcon(!visible)}
                  onFocus={() => togglePasswordReqs(true)}
                  onBlur={() => togglePasswordReqs(false)}
                />
              </div>
            </Form.Element>
          </Grid.Column>
          <Grid.Column widthXS={Columns.Twelve}>
            <Form.Element
              label="Confirm Password"
              htmlFor="confirmPassword"
              required={true}
              errorMessage={confirmPasswordError}
            >
              <VisibilityInput
                id="confirmPassword"
                testID="passwordConfirm--input"
                value={confirmPassword}
                size={ComponentSize.Large}
                onChange={handleInputChange}
                visible={visible}
                status={
                  confirmPasswordError
                    ? ComponentStatus.Error
                    : ComponentStatus.Default
                }
                onToggleClick={() => toggleIcon(!visible)}
              />
            </Form.Element>
          </Grid.Column>
        </Grid.Row>
      </Grid>
      <Button
        className="create-account--button"
        text="Create Account"
        color={ComponentColor.Primary}
        size={ComponentSize.Large}
        type={ButtonType.Submit}
        status={buttonStatus}
        shape={ButtonShape.StretchToFit}
      />
      <Popover.Popover
        ref={passwordRef}
        triggerRef={passwordTriggerRef}
        visible={passwordReqsVisible}
        contents={() => (
          <ul>
            <li>Must be at least 8 characters.</li>
            <li>Must not contain personal information.</li>
            <li>Must not be a common or previous password.</li>
          </ul>
        )}
        showEvent={PopoverInteraction.None}
        hideEvent={PopoverInteraction.None}
        position={PopoverPosition['ToTheRight']}
        color={ComponentColor['Primary']}
        appearance={Appearance['Outline']}
      />
    </>
  )
}

export default SignUpForm
